// ── FSA-156EZ: THE FARM RECORD, READ OUT OF FSA'S OWN DOCUMENT ───────────────────────────
//
// Kevin, 2026-10-26: "We got a 156EZ from a customer. Can you build a tool where we can upload
// those and display the base acre data in a usable format … it needs to save to each farmer."
//
// The FSA-156EZ ("Abbreviated 156 Farm Record") is one section per FSA FARM: the farm's land
// data, its ARC/PLC election (and the default FSA applies where none was made), its DCP crop
// data (base acres, CCC-505 CRP reduction, PLC yield), then one block per TRACT with the
// tract's physical location, owners, land data and its own share of the base acres.
//
// ⚠ IT IS READ, NOT DESCRIBED. Every figure comes out of the document's text layer by a
// deterministic parser — no model reads it — and the document then has to agree with itself
// before anyone is told it was read correctly:
//
//     Σ crop base acres on a farm      = the farm's TOTAL row
//     Σ a tract's crop base acres      = the tract's TOTAL row
//     Σ every tract's base for a crop  = the farm's base for that crop   (and CRP reduction)
//     a tract's PLC yield              = the farm's PLC yield for that crop
//     Σ every tract's land columns     = the farm's land columns
//     tracts read                      = the farm's "Number Of Tracts"
//     pages read                       = "Page: x of N", every x, for every farm
//
// A failed identity is NAMED and the save carries it visibly — rows are never dropped to make
// the arithmetic come out. A line the parser does not recognise is COUNTED and returned,
// never skipped: a tie-out that quietly drops what it could not read reports a clean bill
// of health for a document it never looked at.
//
// ⚠ AN ELECTION AND A DEFAULT ARE DIFFERENT FACTS. The form prints what the producer ELECTED
// (ARC Individual / ARC County / PLC) and, separately, what FSA applies by DEFAULT where no
// election was made. On the CY2026 Du Mond record every elected cell reads "None" and only the
// defaults are populated. Both are stored and the screen says which is which.
//
// ⚠ THE ARC-CO COUNTY IS WHERE THE TRACT SITS, NOT THE FARM'S ADMINISTRATIVE COUNTY. A farm
// administered in Cayuga can hold tracts in Wayne and Seneca; each tract states its own
// "FSA Physical Location". baseAcreCountiesOf() reads it per tract, for the ARC/PLC engine's
// one FSA seam (arcPlcFarmSummary.js countyBasisOf).
//
// CommonJS, no browser APIs — App.jsx imports it and tools/check-fsa-156ez.js requires it as-is.

const PARSER_VERSION = "fsa156ez-1";

const cents = (v) => (v == null ? null : Math.round(Number(v) * 100));
const num = (s) => {
  if (s == null) return null;
  const t = String(s).replace(/,/g, "").trim();
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : null;
};
const clean = (s) => String(s == null ? "" : s).replace(/\s+/g, " ").trim();

// USPS state codes. The ARC/PLC engine keys counties by the two-letter state.
const STATE_CODE = {
  ALABAMA: "AL", ALASKA: "AK", ARIZONA: "AZ", ARKANSAS: "AR", CALIFORNIA: "CA", COLORADO: "CO", CONNECTICUT: "CT",
  DELAWARE: "DE", FLORIDA: "FL", GEORGIA: "GA", HAWAII: "HI", IDAHO: "ID", ILLINOIS: "IL", INDIANA: "IN", IOWA: "IA",
  KANSAS: "KS", KENTUCKY: "KY", LOUISIANA: "LA", MAINE: "ME", MARYLAND: "MD", MASSACHUSETTS: "MA", MICHIGAN: "MI",
  MINNESOTA: "MN", MISSISSIPPI: "MS", MISSOURI: "MO", MONTANA: "MT", NEBRASKA: "NE", NEVADA: "NV", "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ", "NEW MEXICO": "NM", "NEW YORK": "NY", "NORTH CAROLINA": "NC", "NORTH DAKOTA": "ND", OHIO: "OH",
  OKLAHOMA: "OK", OREGON: "OR", PENNSYLVANIA: "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC", "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN", TEXAS: "TX", UTAH: "UT", VERMONT: "VT", VIRGINIA: "VA", WASHINGTON: "WA", "WEST VIRGINIA": "WV",
  WISCONSIN: "WI", WYOMING: "WY",
};
function stateCode(name) {
  const k = clean(name).toUpperCase();
  return STATE_CODE[k] || (/^[A-Z]{2}$/.test(k) ? k : null);
}

// The crop as the rest of SILAS names it. The DCP table prints a full name ("Soybeans"); the
// election cells print FSA's abbreviation ("SOYBN"). Only the abbreviations seen on a real
// 156EZ are mapped; anything else keeps its own spelling rather than a guessed one.
const ELECTION_CROP = { CORN: "CORN", SOYBN: "SOYBEANS", WHEAT: "WHEAT", OATS: "OATS" };
const cropKey = (name) => clean(name).toUpperCase();
function electionCrops(cell) {
  const c = clean(cell);
  if (!c || /^none$/i.test(c)) return [];
  return c.split(/\s*,\s*/).filter(Boolean).map((x) => ELECTION_CROP[x.toUpperCase()] || x.toUpperCase());
}

// ── text items → lines ───────────────────────────────────────────────────────────────────
// pdf.js hands back positioned text items. FSA's form prints a label and its value a point or
// two apart vertically, so grouping on the EXACT y (as the generic pdfText does) splits
// "Tract Number :" from its own "28736" and the value lands on the line above. Items within
// `yTolerance` of a row's first item are one row. Measured on the Du Mond record: every
// label/value pair sits within 1.5pt and no two real rows are closer than 2.8pt.
function linesFromTextItems(items, { yTolerance = 2 } = {}) {
  const its = (items || [])
    .filter((it) => it && typeof it.str === "string" && it.str.trim())
    .map((it) => ({ y: (it.transform && it.transform[5]) || 0, x: (it.transform && it.transform[4]) || 0, w: it.width || 0, s: it.str }))
    .sort((a, b) => b.y - a.y);
  const rows = [];
  for (const it of its) {
    const r = rows[rows.length - 1];
    if (r && r.y0 - it.y <= yTolerance) r.items.push(it);
    else rows.push({ y0: it.y, items: [it] });
  }
  return rows.map(({ items: row }) => {
    const sorted = row.sort((a, b) => a.x - b.x);
    let line = "", cursor = null;
    for (const r of sorted) {
      if (cursor != null) {
        const gap = r.x - cursor;
        line += gap > 6 ? "   " : (gap > 0.5 || /\S$/.test(line) ? " " : "");
      }
      line += r.s;
      cursor = r.x + r.w;
    }
    return line.replace(/[ \t]+$/, "");
  });
}

// ── the parser ───────────────────────────────────────────────────────────────────────────
const LAND1_FARM = ["farmland", "cropland", "dcp_cropland", "wbp", "ewp", "wrp", "grp", "sugarcane"];
const LAND2 = ["state_conservation", "other_conservation", "effective_dcp_cropland", "double_cropped", "crp", "mpl", "dcp_ag_rel_activity", "sod"];
const LAND_FIELDS = [...LAND1_FARM, ...LAND2];

const FARM_KEYS = {
  "Operator Name": "operator_name",
  "CRP Contract Number(s)": "crp_contracts",
  "Recon ID": "recon_id",
  "Transferred From": "transferred_from",
  "ARCPLC G/I/F Eligibility": "arcplc_eligibility",
};
const TRACT_KEYS = {
  "Tract Number": "tract_number",
  "Description": "description",
  "FSA Physical Location": "fsa_physical_location",
  "ANSI Physical Location": "ansi_physical_location",
  "BIA Unit Range Number": "bia_unit_range",
  "CRP Contract Number(s)": "crp_contracts",
  "HEL Status": "hel_status",
  "Wetland Status": "wetland_status",
  "WL Violations": "wl_violations",
  "Owners": "owners",
  "Other Producers": "other_producers",
  "Recon ID": "recon_id",
};
const KEY_RE = new RegExp("^(" + [...new Set([...Object.keys(FARM_KEYS), ...Object.keys(TRACT_KEYS)])]
  .sort((a, b) => b.length - a.length).map((k) => k.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&")).join("|") + ")\\s*:\\s*(.*)$");

// Column headings of the land / election / crop tables. A line made of nothing but these is
// a heading, recognised and read past; anything left over makes it an UNPARSED line.
const LABEL_PHRASES = [
  "United States Department of Agriculture", "Farm Service Agency", "Abbreviated 156 Farm Record",
  "Farm Land Data", "Tract Land Data", "Crop Election Choice", "DCP Crop Data",
  "Effective DCP Cropland", "DCP Cropland", "DCP Ag.Rel.", "DCP Ag. Rel", "Double Cropped", "Farm Status", "Number Of",
  "Farmland", "Farm Land", "Cropland", "WBP", "EWP", "WRP", "GRP", "Sugarcane", "Tracts", "State", "Other",
  "Conservation", "Activity", "CRP", "MPL", "SOD",
  "Price Loss Coverage - Default", "ARC Individual - Default", "ARC County - Default",
  "Price Loss Coverage", "ARC Individual", "ARC County",
  "CCC-505", "Reduction", "Crop Name", "Base Acres", "Acres", "PLC Yield", "PLC", "Yield", "HIP",
].sort((a, b) => b.length - a.length);
function isLabelLine(line) {
  let s = " " + clean(line) + " ";
  for (const p of LABEL_PHRASES) s = s.split(p).join(" ");
  return !s.trim();
}

const BOILER_START = /^In accordance with Federal civil rights law/;
const BOILER_END = /equal opportunity provider, employer, and lender\.\s*$/;
const NUM_TOKEN = /^-?[\d,]+(\.\d+)?$/;

function newFarm(farmNumber) {
  return {
    farm_number: farmNumber, admin_state: null, admin_county: null, prepared: null, crop_year: null,
    operator_name: null, crp_contracts: null, recon_id: null, transferred_from: null, arcplc_eligibility: null,
    farm_status: null, number_of_tracts: null, land: {},
    elections: null, defaults: null, elections_raw: null, defaults_raw: null,
    crops: [], totals: null, tracts: [],
    pages_declared: null, pages_seen: [],
    header_conflicts: [],
  };
}
function newTract(tractNumber) {
  return {
    tract_number: tractNumber, description: null, fsa_physical_location: null, ansi_physical_location: null,
    bia_unit_range: null, crp_contracts: null, hel_status: null, wetland_status: null, wl_violations: null,
    owners: null, other_producers: null, recon_id: null, land: {}, crops: [], totals: null,
    continued_pages: 0,
  };
}

// "NEW YORK/CAYUGA" → { state, county }
function splitLocation(v) {
  const m = clean(v).match(/^(.+?)\s*\/\s*(.+)$/);
  return m ? { state: m[1].toUpperCase(), county: m[2].toUpperCase() } : { state: null, county: null };
}

// The header repeated at the top of every page: state, county, FARM, Prepared, Crop Year.
function readHeader(lines) {
  const out = { farm_number: null, prepared: null, crop_year: null, state: null, county: null };
  const rest = [];
  for (const raw of lines) {
    let l = raw;
    const fm = l.match(/FARM\s*:\s*(\d+)/); if (fm) { out.farm_number = fm[1]; l = l.replace(fm[0], " "); }
    const pm = l.match(/Prepared\s*:\s*(.+)$/); if (pm) { out.prepared = clean(pm[1]); l = l.replace(pm[0], " "); }
    const cy = l.match(/Crop Year\s*:\s*(\d{4})/); if (cy) { out.crop_year = Number(cy[1]); l = l.replace(cy[0], " "); }
    l = l.replace(/Form:\s*FSA-156EZ/, " ").replace(/See Page \d+ for non-discriminatory Statements\./, " ");
    for (const p of ["United States Department of Agriculture", "Farm Service Agency", "Abbreviated 156 Farm Record"]) l = l.split(p).join(" ");
    for (const piece of l.split(/\s{2,}/)) if (clean(piece)) rest.push(clean(piece));
  }
  out.state = rest[0] ? rest[0].toUpperCase() : null;
  out.county = rest[1] ? rest[1].toUpperCase() : null;
  if (rest.length > 2) out.extra = rest.slice(2);
  return out;
}
// "10/20/26 1:26 PM CST" → "2026-10-20"
function preparedDate(s) {
  const m = clean(s).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return null;
  const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  return `${y}-${String(m[1]).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
}

const CROP_ROW = /^([A-Za-z][A-Za-z .&()\/-]*?)\s+(-?[\d,]+\.\d+)\s+(-?[\d,]+\.\d+)(?:\s+(-?[\d,]+(?:\.\d+)?))?(?:\s+(-?[\d,]+(?:\.\d+)?))?$/;

function parse156ez(text) {
  const lines = String(text || "").replace(/\f/g, "\n").split(/\r?\n/).map((l) => l.replace(/\s+$/, ""));
  const farms = [];
  const unparsed = [];
  const wrapped = [];
  let recognized = 0;
  let farm = null, tract = null;
  let land = null;            // { target, row: 1|2 }
  let election = null;        // "elected" | "default" — the next non-heading line holds its values
  let cropTarget = null;      // the farm or tract whose DCP crop table is open
  let lastKey = null;         // the key:value field a wrapped line continues
  let inBoiler = false;
  let page = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const t = line.trim();
    if (!t) continue;
    if (inBoiler) { recognized++; if (BOILER_END.test(t)) inBoiler = false; continue; }
    if (BOILER_START.test(t)) { recognized++; inBoiler = !BOILER_END.test(t); continue; }

    // ── page header ──
    if (/FARM\s*:\s*\d+/.test(t)) {
      const block = [t];
      let j = i + 1;
      // the header runs to "Abbreviated 156 Farm Record" (never more than 8 lines)
      if (!/Abbreviated 156 Farm Record/.test(t)) {
        for (; j < lines.length && j < i + 9; j++) {
          if (!lines[j].trim()) continue;
          block.push(lines[j].trim());
          if (/Abbreviated 156 Farm Record/.test(lines[j])) { j++; break; }
        }
      }
      // "See Page N …" can trail the header on its own line
      if (j < lines.length && /^See Page \d+ for non-discriminatory Statements\.$/.test(lines[j].trim())) { block.push(lines[j].trim()); j++; }
      const h = readHeader(block);
      recognized += block.length;
      i = j - 1;
      page++;
      if (!farm || farm.farm_number !== h.farm_number) {
        farm = newFarm(h.farm_number);
        farms.push(farm);
        tract = null; land = null; election = null; cropTarget = null; lastKey = null;
        farm.admin_state = h.state; farm.admin_county = h.county; farm.prepared = h.prepared; farm.crop_year = h.crop_year;
      } else {
        for (const [k, v] of [["admin_state", h.state], ["admin_county", h.county], ["crop_year", h.crop_year]]) {
          if (v != null && farm[k] != null && String(v) !== String(farm[k])) farm.header_conflicts.push({ field: k, first: farm[k], later: v });
        }
      }
      if (h.extra) for (const x of h.extra) unparsed.push({ page, line: x, where: "page header" });
      continue;
    }
    if (!farm) { unparsed.push({ page, line: t, where: "before the first farm" }); continue; }

    let m;
    if ((m = t.match(/^Page:\s*(\d+)\s+of\s+(\d+)$/))) {
      recognized++;
      farm.pages_seen.push(Number(m[1]));
      if (farm.pages_declared != null && farm.pages_declared !== Number(m[2])) farm.header_conflicts.push({ field: "pages", first: farm.pages_declared, later: Number(m[2]) });
      farm.pages_declared = Number(m[2]);
      continue;
    }
    if ((m = t.match(/^Tract\s+(\d+)\s+Continued\s*\.*$/))) {
      recognized++;
      if (tract && tract.tract_number === m[1]) tract.continued_pages++;
      else unparsed.push({ page, line: t, where: "a continuation for a tract that is not the one being read" });
      continue;
    }
    if (t === "NOTES") { recognized++; cropTarget = null; lastKey = null; continue; }

    // ── key : value ──
    if ((m = t.match(KEY_RE))) {
      recognized++;
      const label = m[1], value = clean(m[2]);
      land = null; election = null; cropTarget = null;
      if (label === "Tract Number") {
        tract = newTract(value || null);
        farm.tracts.push(tract);
        lastKey = "tract_number";
        continue;
      }
      if (tract && TRACT_KEYS[label]) { tract[TRACT_KEYS[label]] = value || null; lastKey = TRACT_KEYS[label]; continue; }
      if (!tract && FARM_KEYS[label]) { farm[FARM_KEYS[label]] = value || null; lastKey = FARM_KEYS[label]; continue; }
      unparsed.push({ page, line: t, where: tract ? "a farm field inside a tract" : "a tract field before any tract" });
      continue;
    }

    if (t === "Farm Land Data") { recognized++; land = { target: farm, row: 1, kind: "farm" }; lastKey = null; continue; }
    if (t === "Tract Land Data") {
      recognized++; lastKey = null;
      if (!tract) { unparsed.push({ page, line: t, where: "tract land data with no tract" }); continue; }
      land = { target: tract, row: 1, kind: "tract" }; continue;
    }
    if (t === "Crop Election Choice") { recognized++; land = null; lastKey = null; continue; }
    if (t === "DCP Crop Data") { recognized++; land = null; election = null; lastKey = null; cropTarget = tract || farm; continue; }

    if (/ARC\s+Individual/.test(t) && isLabelLine(t)) {
      recognized++; land = null; lastKey = null;
      election = /Default/.test(t) ? "default" : "elected";
      continue;
    }
    if (isLabelLine(t)) { recognized++; continue; }

    // ── numeric land rows ──
    const toks = t.split(/\s+/);
    if (land && toks.length >= 8 && toks.slice(0, 8).every((x) => NUM_TOKEN.test(x))) {
      const tgt = land.target;
      if (land.row === 1) {
        LAND1_FARM.forEach((f, k) => { tgt.land[f] = num(toks[k]); });
        if (land.kind === "farm") {
          const tail = toks.slice(8);
          if (tail.length >= 2 && /^\d+$/.test(tail[tail.length - 1])) {
            farm.number_of_tracts = Number(tail[tail.length - 1]);
            farm.farm_status = tail.slice(0, -1).join(" ");
          } else { unparsed.push({ page, line: t, where: "farm land data: no status / tract count" }); }
        } else if (toks.length !== 8) unparsed.push({ page, line: t, where: "tract land data: unexpected trailing columns" });
        land.row = 2; recognized++; continue;
      }
      if (land.row === 2 && toks.length === 8) {
        LAND2.forEach((f, k) => { tgt.land[f] = num(toks[k]); });
        land = null; recognized++; continue;
      }
    }

    // ── election values ──
    if (election) {
      const cells = t.split(/\s{2,}/).map(clean).filter(Boolean);
      if (cells.length === 3) {
        const v = { arc_individual: electionCrops(cells[0]), arc_county: electionCrops(cells[1]), plc: electionCrops(cells[2]) };
        if (election === "default") { farm.defaults = v; farm.defaults_raw = cells.join(" | "); }
        else { farm.elections = v; farm.elections_raw = cells.join(" | "); }
        election = null; recognized++; continue;
      }
      unparsed.push({ page, line: t, where: "election values: expected three columns" });
      election = null;
      continue;
    }

    // ── DCP crop rows ──
    if (cropTarget) {
      if ((m = t.match(/^TOTAL\s+(-?[\d,]+\.\d+)\s+(-?[\d,]+\.\d+)$/))) {
        cropTarget.totals = { base_acres: num(m[1]), crp_reduction_acres: num(m[2]) };
        cropTarget = null; recognized++; continue;
      }
      if ((m = t.match(CROP_ROW)) && !/^TOTAL$/i.test(m[1].trim())) {
        cropTarget.crops.push({ crop: cropKey(m[1]), crop_label: clean(m[1]), base_acres: num(m[2]), crp_reduction_acres: num(m[3]),
          plc_yield: m[4] != null ? num(m[4]) : null, hip: m[5] != null ? num(m[5]) : null });
        recognized++; continue;
      }
    }

    // A wrapped NAME LIST: a line with no colon straight after "Owners :" / "Other Producers :"
    // continues it (a long owner list runs onto a second line). Only those two — anywhere else an
    // unknown line is UNREAD, not quietly glued onto the field above — and every join is recorded
    // so the screen can show it.
    if (tract && (lastKey === "owners" || lastKey === "other_producers") && !/:/.test(t)) {
      tract[lastKey] = clean((tract[lastKey] || "") + " " + t);
      wrapped.push({ page, line: t, field: lastKey, tract: tract.tract_number, farm: farm.farm_number });
      recognized++;
      continue;
    }
    unparsed.push({ page, line: t, where: tract ? `farm ${farm.farm_number} tract ${tract.tract_number}` : `farm ${farm.farm_number}` });
  }

  // locations, owners as lists
  for (const f of farms) for (const tr of f.tracts) {
    const loc = splitLocation(tr.fsa_physical_location);
    tr.location_state = loc.state; tr.location_county = loc.county;
    const ansi = splitLocation(tr.ansi_physical_location);
    tr.ansi_state = ansi.state; tr.ansi_county = ansi.county;
  }
  const preparedDates = farms.map((f) => preparedDate(f.prepared)).filter(Boolean).sort();
  const years = [...new Set(farms.map((f) => f.crop_year).filter((y) => y != null))];
  return {
    parser_version: PARSER_VERSION,
    crop_year: years.length === 1 ? years[0] : null,
    crop_years: years,
    prepared_date: preparedDates.length ? preparedDates[preparedDates.length - 1] : null,
    farms,
    unparsed,
    wrapped,
    recognized_lines: recognized,
  };
}

// ── the identities ───────────────────────────────────────────────────────────────────────
function reconcile156ez(doc) {
  const checks = [];
  const add = (c) => checks.push(c);
  const eqc = (a, b) => cents(a) === cents(b);
  if (!doc || !doc.farms || !doc.farms.length) add({ ok: false, kind: "no_farms", detail: "no FSA farm could be read from this document" });
  if (doc && doc.crop_years && doc.crop_years.length > 1) add({ ok: false, kind: "crop_year", detail: "the farms state different crop years: " + doc.crop_years.join(", ") });
  if (doc && doc.unparsed && doc.unparsed.length) add({ ok: false, kind: "unparsed", detail: doc.unparsed.length + " line(s) were not read" });
  for (const w of (doc && doc.wrapped) || []) add({ ok: true, note: true, kind: "wrapped", farm: w.farm, tract: w.tract, detail: `farm ${w.farm} tract ${w.tract}: "${w.line}" read as the rest of ${w.field === "owners" ? "Owners" : "Other Producers"}` });
  for (const f of (doc && doc.farms) || []) {
    const F = "farm " + f.farm_number;
    // pages: every "x of N", 1..N, exactly once
    const seen = [...f.pages_seen].sort((a, b) => a - b);
    const pagesOk = f.pages_declared != null && seen.length === f.pages_declared && seen.every((p, k) => p === k + 1);
    add({ ok: pagesOk, kind: "pages", farm: f.farm_number, detail: `${F}: read pages ${seen.join(",") || "none"} of ${f.pages_declared == null ? "?" : f.pages_declared}` });
    for (const c of f.header_conflicts) add({ ok: false, kind: "header", farm: f.farm_number, detail: `${F}: page headers disagree on ${c.field} (${c.first} vs ${c.later})` });
    add({ ok: f.number_of_tracts != null && f.number_of_tracts === f.tracts.length, kind: "tract_count", farm: f.farm_number,
      expected: f.number_of_tracts, got: f.tracts.length, detail: `${F}: ${f.tracts.length} tract(s) read, form states ${f.number_of_tracts == null ? "?" : f.number_of_tracts}` });
    // farm crop table sums to its TOTAL
    if (f.crops.length || f.totals) {
      const sb = f.crops.reduce((a, c) => a + cents(c.base_acres), 0), sc = f.crops.reduce((a, c) => a + cents(c.crp_reduction_acres), 0);
      add({ ok: !!f.totals && sb === cents(f.totals.base_acres), kind: "farm_total", farm: f.farm_number, expected: f.totals ? f.totals.base_acres : null, got: sb / 100,
        detail: `${F}: crops sum to ${(sb / 100).toFixed(2)} base acres, TOTAL row ${f.totals ? f.totals.base_acres.toFixed(2) : "missing"}` });
      add({ ok: !!f.totals && sc === cents(f.totals.crp_reduction_acres), kind: "farm_crp_total", farm: f.farm_number,
        detail: `${F}: CRP reduction sums to ${(sc / 100).toFixed(2)}, TOTAL row ${f.totals ? f.totals.crp_reduction_acres.toFixed(2) : "missing"}` });
    }
    for (const tr of f.tracts) {
      const T = `${F} tract ${tr.tract_number}`;
      if (tr.crops.length || tr.totals) {
        const sb = tr.crops.reduce((a, c) => a + cents(c.base_acres), 0);
        add({ ok: !!tr.totals && sb === cents(tr.totals.base_acres), kind: "tract_total", farm: f.farm_number, tract: tr.tract_number,
          detail: `${T}: crops sum to ${(sb / 100).toFixed(2)}, TOTAL row ${tr.totals ? tr.totals.base_acres.toFixed(2) : "missing"}` });
      }
    }
    // per crop: Σ tracts = farm; PLC yield agrees
    const crops = new Set([...f.crops.map((c) => c.crop), ...f.tracts.flatMap((t) => t.crops.map((c) => c.crop))]);
    for (const crop of crops) {
      const fc = f.crops.find((c) => c.crop === crop);
      const tb = f.tracts.reduce((a, t) => a + t.crops.filter((c) => c.crop === crop).reduce((x, c) => x + cents(c.base_acres), 0), 0);
      const tc = f.tracts.reduce((a, t) => a + t.crops.filter((c) => c.crop === crop).reduce((x, c) => x + cents(c.crp_reduction_acres), 0), 0);
      add({ ok: !!fc && tb === cents(fc.base_acres), kind: "crop_by_tract", farm: f.farm_number, crop, expected: fc ? fc.base_acres : null, got: tb / 100,
        detail: `${F} ${crop}: tracts sum to ${(tb / 100).toFixed(2)} base acres, farm states ${fc ? fc.base_acres.toFixed(2) : "no such crop"}` });
      add({ ok: !!fc && tc === cents(fc.crp_reduction_acres), kind: "crp_by_tract", farm: f.farm_number, crop,
        detail: `${F} ${crop}: tracts sum to ${(tc / 100).toFixed(2)} CRP reduction acres, farm states ${fc ? fc.crp_reduction_acres.toFixed(2) : "no such crop"}` });
      for (const t of f.tracts) for (const c of t.crops.filter((c) => c.crop === crop)) {
        if (fc && c.plc_yield != null && fc.plc_yield != null && c.plc_yield !== fc.plc_yield) {
          add({ ok: true, note: true, kind: "plc_yield", farm: f.farm_number, tract: t.tract_number, crop, detail: `${F} tract ${t.tract_number} ${crop}: PLC yield ${c.plc_yield} vs the farm's ${fc.plc_yield}` });
        }
      }
    }
    // land: Σ tracts = farm, every column
    if (f.tracts.length) {
      for (const k of LAND_FIELDS) {
        const fv = f.land[k];
        const tv = f.tracts.reduce((a, t) => a + (t.land[k] == null ? NaN : cents(t.land[k])), 0);
        add({ ok: fv != null && Number.isFinite(tv) && tv === cents(fv), kind: "land", farm: f.farm_number, field: k,
          detail: `${F} ${k}: tracts sum to ${Number.isFinite(tv) ? (tv / 100).toFixed(2) : "unread"}, farm states ${fv == null ? "unread" : fv.toFixed(2)}` });
      }
    }
  }
  const failures = checks.filter((c) => !c.ok);
  return { ok: failures.length === 0 && checks.length > 0, checks, failures };
}

// ── rows for storage ─────────────────────────────────────────────────────────────────────
// One payload per farmer-year for the fsa_156ez_replace() RPC (sql/110). Every row of one kind
// carries the identical key set (PostgREST refuses mixed shapes; the RPC reads jsonb keys).
function landCols(land) { const o = {}; for (const k of LAND_FIELDS) o[k] = land && land[k] != null ? land[k] : null; return o; }
function rowsFor156ez(doc, rec) {
  const farms = (doc.farms || []).map((f) => ({
    farm_number: f.farm_number,
    admin_state: f.admin_state, admin_county: f.admin_county,
    operator_name: f.operator_name, crp_contracts: f.crp_contracts, recon_id: f.recon_id,
    transferred_from: f.transferred_from, arcplc_eligibility: f.arcplc_eligibility,
    farm_status: f.farm_status, number_of_tracts: f.number_of_tracts,
    prepared: f.prepared,
    ...landCols(f.land),
    elections: f.elections || null, defaults: f.defaults || null,
    elections_raw: f.elections_raw || null, defaults_raw: f.defaults_raw || null,
    tracts: f.tracts.map((t) => ({
      tract_number: t.tract_number, description: t.description,
      location_state: t.location_state, location_county: t.location_county,
      ansi_state: t.ansi_state, ansi_county: t.ansi_county,
      bia_unit_range: t.bia_unit_range, crp_contracts: t.crp_contracts, hel_status: t.hel_status,
      wetland_status: t.wetland_status, wl_violations: t.wl_violations,
      owners: t.owners, other_producers: t.other_producers, recon_id: t.recon_id,
      ...landCols(t.land),
    })),
    base_acres: [
      ...f.crops.map((c) => ({ tract_number: null, crop: c.crop, base_acres: c.base_acres, crp_reduction_acres: c.crp_reduction_acres, plc_yield: c.plc_yield, hip: c.hip })),
      ...f.tracts.flatMap((t) => t.crops.map((c) => ({ tract_number: t.tract_number, crop: c.crop, base_acres: c.base_acres, crp_reduction_acres: c.crp_reduction_acres, plc_yield: c.plc_yield, hip: c.hip }))),
    ],
  }));
  const rc = reconcile156ez(doc);
  return {
    farmer_id: rec.farmerId,
    crop_year: doc.crop_year,
    document_id: rec.documentId || null,
    storage_path: rec.storagePath || null,
    file_name: rec.fileName || null,
    prepared_date: doc.prepared_date,
    parser_version: doc.parser_version,
    identity_ok: rc.ok,
    identity_failures: rc.failures.map((c) => c.detail),
    unparsed: (doc.unparsed || []).slice(0, 200),
    unparsed_count: (doc.unparsed || []).length,
    farms,
  };
}

// ── what the screen shows ────────────────────────────────────────────────────────────────
// `farms` are fsa_farm_records rows; `base` are fsa_base_acres rows (farm-level rows have a
// NULL tract). Farmer totals by crop come from the FARM-level rows only — summing tract rows
// too would count every acre twice. PLC yield is acre-weighted on base acres and is labelled
// as such: it is a summary of several FSA farms' yields, never one FSA figure.
function farmerTotals(base) {
  const by = new Map();
  for (const r of base || []) {
    if (r.tract_number != null && r.tract_number !== "") continue;
    const g = by.get(r.crop) || { crop: r.crop, base_cents: 0, crp_cents: 0, wy: 0, wyAcres: 0, farms: 0 };
    g.base_cents += cents(r.base_acres) || 0;
    g.crp_cents += cents(r.crp_reduction_acres) || 0;
    if (r.plc_yield != null && Number(r.base_acres) > 0) { g.wy += Number(r.plc_yield) * Number(r.base_acres); g.wyAcres += Number(r.base_acres); }
    g.farms++;
    by.set(r.crop, g);
  }
  return [...by.values()].map((g) => ({ crop: g.crop, base_acres: g.base_cents / 100, crp_reduction_acres: g.crp_cents / 100, farms: g.farms,
    plc_yield_weighted: g.wyAcres > 0 ? Math.round((g.wy / g.wyAcres) * 10) / 10 : null }))
    .sort((a, b) => b.base_acres - a.base_acres || a.crop.localeCompare(b.crop));
}

// The ARC/PLC engine's FSA seam: { "CROP|ST": ["COUNTY", …] } — the counties where this
// farmer's BASE ACRES physically sit, read per tract. A tract with no base for the crop does
// not name its county for that crop.
function baseAcreCountiesOf(tracts, base) {
  const loc = new Map();
  for (const t of tracts || []) loc.set(String(t.farm_record_id || t.farm_number) + "|" + t.tract_number, t);
  const out = {};
  for (const r of base || []) {
    if (r.tract_number == null || r.tract_number === "" || !(Number(r.base_acres) > 0)) continue;
    const t = loc.get(String(r.farm_record_id || r.farm_number) + "|" + r.tract_number);
    if (!t) continue;
    const st = stateCode(t.location_state), co = clean(t.location_county).toUpperCase();
    if (!st || !co) continue;
    const k = String(r.crop).toUpperCase() + "|" + st;
    (out[k] = out[k] || []);
    if (!out[k].includes(co)) out[k].push(co);
  }
  for (const k of Object.keys(out)) out[k].sort();
  return out;
}

// CSV: one line per base-acre row, farm-level rows first, with the farm and tract context.
function csvCell(v) { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; }
function csv156ez(farms, tracts, base) {
  const farmById = new Map((farms || []).map((f) => [f.id, f]));
  const tractKey = new Map((tracts || []).map((t) => [t.farm_record_id + "|" + t.tract_number, t]));
  const head = ["crop_year", "farm_number", "admin_state", "admin_county", "level", "tract_number", "tract_location_state", "tract_location_county", "owners",
    "crop", "base_acres", "crp_reduction_acres", "plc_yield", "farm_cropland", "tract_cropland", "elected_arc_county", "elected_plc", "default_arc_county", "default_plc"];
  const out = [head.join(",")];
  const rows = (base || []).slice().sort((a, b) => {
    const fa = farmById.get(a.farm_record_id) || {}, fb = farmById.get(b.farm_record_id) || {};
    return (Number(fa.farm_number) - Number(fb.farm_number)) || ((a.tract_number == null ? -1 : Number(a.tract_number)) - (b.tract_number == null ? -1 : Number(b.tract_number))) || String(a.crop).localeCompare(String(b.crop));
  });
  for (const r of rows) {
    const f = farmById.get(r.farm_record_id) || {};
    const t = r.tract_number != null ? tractKey.get(r.farm_record_id + "|" + r.tract_number) || {} : {};
    const el = f.elections || {}, df = f.defaults || {};
    out.push([f.crop_year, f.farm_number, f.admin_state, f.admin_county, r.tract_number == null ? "farm" : "tract", r.tract_number, t.location_state, t.location_county, t.owners,
      r.crop, r.base_acres, r.crp_reduction_acres, r.plc_yield, f.cropland, t.cropland,
      (el.arc_county || []).join(" "), (el.plc || []).join(" "), (df.arc_county || []).join(" "), (df.plc || []).join(" ")].map(csvCell).join(","));
  }
  return out.join("\n");
}

// ── ONE MODEL, TWO CALLERS: the screen and the printed summary ─────────────────────────────
// `stored` is { upload, farms, tracts, base } as read from the four tables. A parse that has
// not been saved yet is turned into the same shape by storedFromPayload, so the preview the
// agent checks before saving and the page shown after it are built by the same function.
function storedFromPayload(payload) {
  const farms = [], tracts = [], base = [];
  (payload.farms || []).forEach((f, i) => {
    const id = "pending-" + i;
    const { tracts: ts, base_acres: bs, ...fr } = f;
    farms.push({ ...fr, id, crop_year: payload.crop_year });
    for (const t of ts || []) tracts.push({ ...t, farm_record_id: id, farm_number: f.farm_number });
    for (const b of bs || []) base.push({ ...b, farm_record_id: id, farm_number: f.farm_number });
  });
  return {
    upload: { crop_year: payload.crop_year, prepared_date: payload.prepared_date, file_name: payload.file_name, identity_ok: payload.identity_ok,
      identity_failures: payload.identity_failures, unparsed_count: payload.unparsed_count, farm_count: farms.length, tract_count: tracts.length },
    farms, tracts, base,
  };
}

const CROP_ORDER = ["CORN", "SOYBEANS", "WHEAT", "OATS", "BARLEY", "SORGHUM"];
const PROGRAM = [["arc_county", "ARC-CO"], ["plc", "PLC"], ["arc_individual", "ARC-IC"]];
// What FSA's record says for one crop on one farm: the producer's ELECTION if one is printed,
// otherwise FSA's DEFAULT, otherwise nothing. The source is carried, never collapsed.
function programFor(farm, crop) {
  for (const [src, set] of [["elected", farm.elections], ["default", farm.defaults]]) {
    if (!set) continue;
    for (const [k, lbl] of PROGRAM) if ((set[k] || []).includes(crop)) return { program: lbl, source: src };
  }
  return { program: null, source: null };
}
// A farm number is only unique WITHIN an FSA administrative county, so every cross-document join
// (the CCC-866 election, the 156EZ comparison) is keyed state|county|farm, upper-cased as printed.
const up = (s) => clean(s).toUpperCase();
function adminKey(f) { return up(f.admin_state != null ? f.admin_state : f.state_name) + "|" + up(f.admin_county != null ? f.admin_county : f.county_name) + "|" + String(Number(f.farm_number)); }

// A document is stored per FARMER + YEAR + ADMIN STATE + ADMIN COUNTY (sql/116): a farmer whose
// farms sit in two FSA county offices holds two documents, and re-uploading one county replaces
// only that county. The 156EZ prints the administrative state and county as NAMES only (no
// codes), so the key is the printed names. A farm whose administrative county cannot be read
// cannot be filed under any county, so a document containing one must not be saved.
function missingAdmin156(doc) {
  return (doc.farms || []).filter((f) => !clean(f.admin_state) || !clean(f.admin_county)).map((f) => f.farm_number);
}
function adminPairs156(doc) {
  const m = new Map();
  for (const f of doc.farms || []) if (clean(f.admin_state) && clean(f.admin_county)) m.set(up(f.admin_state) + "|" + up(f.admin_county), { admin_state: up(f.admin_state), admin_county: up(f.admin_county) });
  return [...m.values()].sort((a, b) => a.admin_county.localeCompare(b.admin_county));
}
// The per-county upload rows of ONE year, combined into the one record the screen and the print use.
function mergeUploads156(rows) {
  const r = (rows || []).slice().sort((a, b) => String(a.admin_county).localeCompare(String(b.admin_county)));
  if (!r.length) return null;
  const dates = r.map((x) => x.prepared_date).filter(Boolean).sort();
  return {
    crop_year: r[0].crop_year, prepared_date: dates.length ? dates[dates.length - 1] : null,
    file_name: [...new Set(r.map((x) => x.file_name).filter(Boolean))].join(", ") || null,
    storage_paths: [...new Set(r.map((x) => x.storage_path).filter(Boolean))],
    identity_ok: r.every((x) => x.identity_ok), identity_failures: [...new Set(r.flatMap((x) => x.identity_failures || []))],
    unparsed_count: r.reduce((a, x) => a + (x.unparsed_count || 0), 0),
    farm_count: r.reduce((a, x) => a + (x.farm_count || 0), 0), tract_count: r.reduce((a, x) => a + (x.tract_count || 0), 0),
    counties: r.map((x) => ({ admin_state: x.admin_state, admin_county: x.admin_county, file_name: x.file_name, prepared_date: x.prepared_date, farm_count: x.farm_count })),
  };
}

function baseAcreModel(stored) {
  const { upload = {}, farms = [], tracts = [], base = [], overrides866 = null } = stored || {};
  const totals = farmerTotals(base);
  const crops = [...new Set(totals.map((t) => t.crop))].sort((a, b) => {
    const ia = CROP_ORDER.indexOf(a), ib = CROP_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
  const tractsBy = new Map();
  for (const t of tracts) { const k = t.farm_record_id; (tractsBy.get(k) || tractsBy.set(k, []).get(k)).push(t); }
  const rows = farms.map((f) => {
    const fBase = base.filter((r) => r.farm_record_id === f.id);
    const byCrop = {};
    for (const r of fBase.filter((r) => r.tract_number == null || r.tract_number === "")) {
      byCrop[r.crop] = { base_acres: r.base_acres, plc_yield: r.plc_yield, crp_reduction_acres: r.crp_reduction_acres, ...programFor(f, r.crop) };
      // The SIGNED CCC-866 election outranks the 156EZ's election-or-default text.
      const ov = overrides866 && overrides866[adminKey(f)] && overrides866[adminKey(f)][r.crop];
      if (ov && ov.program) byCrop[r.crop] = { ...byCrop[r.crop], program: ov.program, source: "ccc866", signed_date: ov.signed_date || null };
    }
    const ts = (tractsBy.get(f.id) || []).slice().sort((a, b) => Number(a.tract_number) - Number(b.tract_number)).map((t) => {
      const tb = {};
      for (const r of fBase.filter((r) => String(r.tract_number) === String(t.tract_number))) tb[r.crop] = { base_acres: r.base_acres, plc_yield: r.plc_yield };
      return { ...t, byCrop: tb, total_base: Object.values(tb).reduce((a, x) => a + (cents(x.base_acres) || 0), 0) / 100 };
    });
    const counties = [...new Set(ts.map((t) => t.location_county).filter(Boolean))];
    return {
      id: f.id, farm_number: f.farm_number, admin_state: f.admin_state, admin_county: f.admin_county,
      counties, out_of_county: counties.filter((c) => c !== f.admin_county),
      cropland: f.cropland, farmland: f.farmland, number_of_tracts: f.number_of_tracts, operator_name: f.operator_name,
      elections: f.elections, defaults: f.defaults,
      no_election_printed: !f.elections || PROGRAM.every(([k]) => !(f.elections[k] || []).length),
      byCrop, total_base: Object.values(byCrop).reduce((a, x) => a + (cents(x.base_acres) || 0), 0) / 100,
      tracts: ts,
    };
  }).sort((a, b) => String(a.admin_state).localeCompare(String(b.admin_state)) || String(a.admin_county).localeCompare(String(b.admin_county)) || Number(a.farm_number) - Number(b.farm_number) || String(a.farm_number).localeCompare(String(b.farm_number)));
  const anyElected = rows.some((r) => Object.values(r.byCrop).some((c) => c.source === "elected"));
  const anyDefault = rows.some((r) => Object.values(r.byCrop).some((c) => c.source === "default"));
  const anyCcc866 = rows.some((r) => Object.values(r.byCrop).some((c) => c.source === "ccc866"));
  return {
    crop_year: upload.crop_year != null ? upload.crop_year : (farms[0] && farms[0].crop_year) || null,
    prepared_date: upload.prepared_date || null, file_name: upload.file_name || null,
    identity_ok: upload.identity_ok, identity_failures: upload.identity_failures || [], unparsed_count: upload.unparsed_count || 0,
    crops, totals,
    total_base: totals.reduce((a, t) => a + cents(t.base_acres), 0) / 100,
    cropland: farms.reduce((a, f) => a + (cents(f.cropland) || 0), 0) / 100,
    counties: upload.counties || [...new Set(rows.map((r) => r.admin_county).filter(Boolean))].map((c) => ({ admin_county: c })), storage_paths: upload.storage_paths || [],
    farm_count: rows.length, tract_count: tracts.length,
    farms_without_base: rows.filter((r) => !Object.keys(r.byCrop).length).map((r) => r.farm_number),
    any_elected: anyElected || anyCcc866, any_default: anyDefault, any_ccc866: anyCcc866,
    farms: rows,
  };
}

module.exports = {
  PARSER_VERSION,
  storedFromPayload,
  baseAcreModel,
  programFor,
  LAND_FIELDS,
  linesFromTextItems,
  parse156ez,
  reconcile156ez,
  rowsFor156ez,
  farmerTotals,
  baseAcreCountiesOf,
  csv156ez,
  electionCrops,
  stateCode,
  preparedDate,
  clean,
  num,
  adminKey,
  missingAdmin156,
  adminPairs156,
  mergeUploads156,
};
